import { useEffect, useRef, useState } from 'react';
import { useOnboarding } from '@/hooks/useOnboarding';
import { useOnboardingReplay } from '@/hooks/useOnboardingReplay';
import {
  ONBOARDING_TOUR_STEP_IDS,
  shouldSkipTour,
  markOnboardingDoneLocal,
  type AcquisitionSource,
} from '@/lib/onboarding';
import { OnboardingSegmentation } from './OnboardingSegmentation';
import { UserGuideTour } from './UserGuideTour';
import { OnboardingActivation, type ActivationResult } from './OnboardingActivation';
import { OnboardingWow } from './OnboardingWow';

type Phase = 'idle' | 'segmentation' | 'tour' | 'activation' | 'wow' | 'done';

export function OnboardingFlow() {
  const {
    needsOnboarding,
    acquisitionSource,
    saveAcquisitionSource,
    completeOnboarding,
  } = useOnboarding();

  const { replayNonce } = useOnboardingReplay();
  const [phase, setPhase] = useState<Phase>('idle');
  const [target, setTarget] = useState<ActivationResult | null>(null);

  // Start the flow once, when we've confirmed the user is genuinely new.
  useEffect(() => {
    if (phase !== 'idle') return;
    if (!needsOnboarding) return;
    if (!acquisitionSource) {
      setPhase('segmentation');
    } else if (shouldSkipTour(acquisitionSource)) {
      setPhase('activation');
    } else {
      setPhase('tour');
    }
  }, [phase, needsOnboarding, acquisitionSource]);

  // Manual replay from dashboard banner ("Completar" / "Cambiar").
  const lastReplay = useRef(replayNonce);
  useEffect(() => {
    if (replayNonce === lastReplay.current) return;
    lastReplay.current = replayNonce;
    setTarget(null);
    setPhase('segmentation');
  }, [replayNonce]);

  const handleSegmentation = async (source: AcquisitionSource) => {
    try {
      await saveAcquisitionSource.mutateAsync(source);
    } catch {
      /* non-blocking: continue the flow even if the write fails */
    }
    setPhase(shouldSkipTour(source) ? 'activation' : 'tour');
  };

  const finish = (score: number | null) => {
    setPhase('done');
    markOnboardingDoneLocal();
    completeOnboarding.mutate(score);
  };

  if (phase === 'idle' || phase === 'done') return null;

  return (
    <>
      <OnboardingSegmentation
        open={phase === 'segmentation'}
        submitting={saveAcquisitionSource.isPending}
        onContinue={handleSegmentation}
      />

      {phase === 'tour' && (
        <UserGuideTour
          open
          onOpenChange={() => {}}
          stepIds={ONBOARDING_TOUR_STEP_IDS}
          finishLabel="Continuar"
          onComplete={() => setPhase('activation')}
        />
      )}

      <OnboardingActivation
        open={phase === 'activation'}
        onActivated={(result) => {
          setTarget(result);
          setPhase('wow');
        }}
        onSkip={() => finish(null)}
      />

      {phase === 'wow' && target && (
        <OnboardingWow
          open
          year={target.year}
          month={target.month}
          providedScore={target.score}
          onFinish={(score) => finish(score)}
        />
      )}
    </>
  );
}
